import { useEffect, useMemo, useState } from 'react';

import {
  Box,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Skeleton,
  Typography,
} from '@mui/material';
import { eachYearOfInterval } from 'date-fns';
import {
  Bar,
  CartesianGrid,
  Tooltip,
  XAxis,
  YAxis,
  BarChart,
  ResponsiveContainer,
} from 'recharts';

import { fetchTemporalBulkStats } from 'api';
import { colors } from 'config/theme';
import {
  annualChartOptions,
  getErrorDetail,
  getMonthDateRanges,
  safeFormatDate,
  getLabel,
} from 'helpers';
import { useSnackContext } from 'hooks/useSnackContext';
import { AnnualChartEnum, TemporalStatsReturn } from 'types';

import { currentYear } from './data';
interface MonthData extends TemporalStatsReturn {
  month: string;
}
interface ChartData {
  new_candidate_count_total: number;
  newly_created_org_count_total: number;
  newly_reviewable_org_count_total: number;
  review_count_total: number;
  signed_candidate_count_total: number;
  tranched_candidate_count_total: number;
  monthly_data: Array<MonthData>;
}
export const AnnualChart = () => {
  const { setSnack } = useSnackContext();
  const [temporalStats, setTemporalStats] = useState<
    Array<TemporalStatsReturn>
  >([]);
  const [chosenYear, setChosenYear] = useState(currentYear);
  const [fetching, setFetching] = useState(false);
  const [chartType, setChartType] = useState<AnnualChartEnum>(
    'new_candidate_count',
  );
  const years = eachYearOfInterval({
    start: new Date(2022, 0, 1),
    end: new Date(),
  });
  const yearOptions = useMemo(() => {
    return years.map((year) => ({
      value: year.getFullYear(),
      label: year.getFullYear().toString(),
    }));
  }, [years]);
  useEffect(() => {
    const getData = async () => {
      try {
        setFetching(true);
        const dateRanges = getMonthDateRanges(chosenYear);
        const { data } = await fetchTemporalBulkStats(dateRanges);
        setTemporalStats(data);
        setFetching(false);
      } catch (e: any) {
        setFetching(false);
        setSnack((props) => ({
          ...props,
          severity: 'error',
          open: true,
          message: getErrorDetail({
            error: e,
            fallback: 'Failed To Bulk Stats',
          }),
          error: e,
        }));
      } finally {
        setFetching(false);
      }
    };
    getData();

    return () => {
      setTemporalStats([]);
      setFetching(false);
    };
  }, [setSnack, chosenYear, setFetching, setChosenYear, setTemporalStats]);
  const chartData = useMemo(() => {
    return temporalStats.reduce<ChartData>(
      (accumulator, currentValue) => ({
        new_candidate_count_total: (accumulator.new_candidate_count_total +=
          currentValue.new_candidate_count),
        newly_created_org_count_total:
          (accumulator.newly_created_org_count_total +=
            currentValue.newly_created_org_count),
        newly_reviewable_org_count_total:
          (accumulator.newly_reviewable_org_count_total +=
            currentValue.newly_reviewable_org_count),
        review_count_total: (accumulator.review_count_total +=
          currentValue.review_count),
        signed_candidate_count_total:
          (accumulator.signed_candidate_count_total +=
            currentValue.signed_candidate_count),
        tranched_candidate_count_total:
          (accumulator.tranched_candidate_count_total +=
            currentValue.tranched_candidate_count),
        monthly_data: [
          ...accumulator.monthly_data,
          {
            ...currentValue,
            month: safeFormatDate(currentValue.end_date, 'LLL'),
          },
        ],
      }),
      {
        new_candidate_count_total: 0,
        newly_created_org_count_total: 0,
        newly_reviewable_org_count_total: 0,
        review_count_total: 0,
        signed_candidate_count_total: 0,
        tranched_candidate_count_total: 0,
        monthly_data: [],
      },
    );
  }, [temporalStats]);

  const returnChartTotal = (chartType: AnnualChartEnum) => {
    const chartTypeTotal = chartType + '_total';
    if (Object.prototype.hasOwnProperty.call(chartData, chartTypeTotal)) {
      return (
        chartData[chartTypeTotal as keyof ChartData].toLocaleString('en-US') +
        ' Total'
      );
    }
    return '--';
  };
  const returnChartDescription = (chartType: AnnualChartEnum) => {
    switch (chartType) {
      case 'new_candidate_count':
        return 'Number of Candidates with at least one work period.';
      case 'newly_created_org_count':
        return 'Number of new Organizations added via data upload from state.';
      case 'newly_reviewable_org_count':
        return 'Number of onboarded Organizations (including their Associated Entities).';
      case 'review_count':
        return 'Number of candidates who have been submitted by employers (whether they were found or not).';
      case 'signed_candidate_count':
        return 'Number of Candidates who have been sent to State in signed tranches.';
      default:
        return '';
    }
  };
  return (
    <>
      <Typography display='inline' sx={{ color: colors.blue }} variant='h6'>
        {getLabel({ value: chartType, options: annualChartOptions }) + ':'}
      </Typography>{' '}
      {fetching ? (
        <CircularProgress size={12} />
      ) : (
        <Typography sx={{ color: colors.uswds.primary.light }} display='inline'>
          {returnChartTotal(chartType)}
        </Typography>
      )}
      <Typography variant='body2'>
        {returnChartDescription(chartType)}
      </Typography>
      {yearOptions.length > 0 && !fetching ? (
        <Paper sx={{ padding: 2, height: '100%' }}>
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            <Box
              sx={{
                display: 'flex',
                width: '100%',
                flexDirection: 'row',
                justifyContent: 'flex-end',
              }}
            >
              <FormControl size='small'>
                <InputLabel id='select-chart-type'>Chart Type</InputLabel>
                <Select
                  labelId='select-chart-type'
                  variant='standard'
                  value={chartType ?? ''}
                  label='Chart Type'
                  onChange={({ target: { value: chartType } }) => {
                    setChartType(chartType as AnnualChartEnum);
                  }}
                >
                  {annualChartOptions.map((option) => (
                    <MenuItem key={option.label} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl size='small'>
                <InputLabel id='select-chart-year'>Year</InputLabel>
                <Select
                  variant='standard'
                  labelId='select-chart-year'
                  value={chosenYear ?? ''}
                  label='Chart Year'
                  sx={{ minWidth: 120, ml: 1 }}
                  onChange={({ target: { value: year } }) => {
                    setChosenYear(year as number);
                  }}
                >
                  {yearOptions.map((option) => (
                    <MenuItem key={option.label} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
            <ResponsiveContainer height={250}>
              <BarChart
                data={chartData.monthly_data}
                margin={{ top: 0, right: 0, left: 10, bottom: 0 }}
              >
                <CartesianGrid vertical={false} strokeDasharray='3 3' />
                <XAxis axisLine={false} dataKey='month' tickLine={false} />
                <YAxis axisLine={false} tickLine={false} />
                <Tooltip />
                <Bar dataKey={chartType} fill={colors.uswds.primary.vivid} />
              </BarChart>
            </ResponsiveContainer>
          </Box>
        </Paper>
      ) : (
        <Skeleton variant='rectangular' width='100%' height={260} />
      )}
    </>
  );
};
